import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { adminApi, tokenStore } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = tokenStore.get();
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const me = await adminApi.me();
            setUser(me);
        } catch {
            tokenStore.clear();
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const data = await adminApi.login(email, password);
        tokenStore.set(data.access_token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await adminApi.logout();
        } catch {
            /* ignore */
        }
        tokenStore.clear();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
};

export const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-sm text-muted-foreground">Cargando…</div>
            </div>
        );
    }
    if (!user) {
        return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }
    return children;
};
